import '../styles/FavouriteTooltip.css';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdvancedSearch from './AdvancedSearch';
import recordService from '../services/RecordService';
import riskService from '../services/RiskService';
import deviceService from '../services/DeviceService';
import RiskLevelModal from './modals/RiskLevelModal';
import FavouriteDeviceModal from './modals/FavouriteDeviceModal';
import { useSite } from '../context/SiteContext';
import { useFilterContext } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';
import { FaExternalLinkAlt, FaUser, FaSort, FaSitemap, FaStar, FaRegStar } from 'react-icons/fa';
import './../styles/RecordsTable.css';
import './../styles/LoadingSpinner.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import useAbortableFetch from '../hooks/useAbortableFetch';
import { UserType } from '../constants/userType';

const normalizeFavouriteFlag = (value) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '0' || normalized === 'false' || normalized === 'no') {
      return false;
    }
    if (normalized === '1' || normalized === 'true' || normalized === 'yes') {
      return true;
    }
  }

  return Boolean(value);
};

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const RecordsTable = () => {
  const { selectedSiteId: groupId } = useSite();
  const { filters, setFilters, setPagination } = useFilterContext();
  const { userType } = useAuth();
  const navigate = useNavigate();
  const isPoliceUser = userType === UserType.POLICE;
  const canShowRiskLevel = userType !== UserType.POLICE;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(200);
  const [totalPages, setTotalPages]   = useState(1);
  const [sortColumn, setSortColumn]   = useState('0');
  const [sortAsc, setSortAsc]         = useState(false);

  const [rows, setRows] = useState([]);
  const [riskNotesByVisor, setRiskNotesByVisor] = useState({});
  const [riskLevelModal, setRiskLevelModal] = useState({ open: false, nominal: null });
  const [favouriteModal, setFavouriteModal] = useState({ open: false, record: null });
  const [groupCount, setGroupCount] = useState(0);
  const [deviceCount, setDeviceCount] = useState(0);

  useEffect(() => {
    setFilters(prev =>
      prev.visorRef ? { ...prev, visorRef: '' } : prev
    );
  }, [setFilters]);

  const debouncedFilters = useDebounce(filters, 300);

  const { data: tableData, error: fetchError, isLoading: fetchLoading } = useAbortableFetch(
    (signal) => {
      if (!groupId) return Promise.resolve(null);

      const isClientSideSort =
        sortColumn === 'isFavourites' ||
        (canShowRiskLevel && (sortColumn === 'riskLevel' || sortColumn === 'category'));

      const payload = {
        ...debouncedFilters,
        pageIndex: currentPage,
        pageSize,
        ...(isClientSideSort ? { sortColumn: '0', sortAsc: false } : { sortColumn, sortAsc }),
      };

      return recordService.getRecordsTable(groupId, payload, signal);
    },
    [canShowRiskLevel, groupId, debouncedFilters, currentPage, pageSize, sortColumn, sortAsc]
  );
 
  useEffect(() => {
    if (!tableData) return;

    const {
      data = [],
      totalCount = 0,
      currentPage: respPage = 1,
      pageSize:    respSize = 200,
      totalPages:  respPages = 1,
      totalDeviceRecords = 0,
    } = tableData;

    const levelMap = { 2: 'High', 1: 'Medium', 0: 'N/A' };
    const processedRows = data.map(item => ({
      ...item,
      isFavourites: normalizeFavouriteFlag(item.isFavourites ?? item.isFavourite),
      riskLevelDisplay: levelMap[item.riskLevel] || 'N/A',
      category: item.category || ''
    }));
    
    setRows(processedRows);
    setGroupCount(totalCount);
    setDeviceCount(totalDeviceRecords);
    setCurrentPage(respPage);
    setPageSize(respSize);
    setTotalPages(respPages);
  }, [tableData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedFilters]);

  const handlePageChange = newPage => {
    const maxPage = totalPages;
    if (newPage < 1 || newPage > maxPage) return;
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = e => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
    window.scrollTo(0, 0);
  };
 
  const handleSort = columnKey => {
    if (sortColumn === columnKey) {
      setSortAsc(asc => !asc);
    } else {
      setSortColumn(columnKey);
      setSortAsc(true);
    }
    setCurrentPage(1);
  };

  const handleTotalClick = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    navigate('/images');
  };

  const handleRecordCountClick = visorRef => {
    setFilters(prev => ({ ...prev, visorRef }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    navigate('/images');
  };

  const handleViewProfile = visorRef => {
    navigate(`/profile?transgressorRef=${visorRef}`);
  };

  const openRiskLevelModal = (item) => {
    setRiskLevelModal({
      open: true,
      nominal: {
        ...item,
        visorId: item.visorRef,
      },
    });
  };

  const closeRiskLevelModal = () => {
    setRiskLevelModal({ open: false, nominal: null });
  };

  const openFavouriteModal = (item) => {
    setFavouriteModal({ open: true, record: item });
  };

  const closeFavouriteModal = () => {
    setFavouriteModal({ open: false, record: null });
  };

  const handleRiskLevelSave = (riskData) => {
    const visorRef = riskLevelModal.nominal?.visorRef || riskLevelModal.nominal?.visorId;
    if (!visorRef) return;

    const nextRiskLevelDisplay =
      riskData.riskLevel === 'high'
        ? 'High'
        : riskData.riskLevel === 'medium'
          ? 'Medium'
          : 'N/A';

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.visorRef === visorRef
          ? {
              ...row,
              riskLevel: riskData.riskLevel === 'high' ? 2 : riskData.riskLevel === 'medium' ? 1 : 0,
              riskLevelDisplay: nextRiskLevelDisplay,
              category: riskData.riskLevel === 'none' ? '' : riskData.category,
              description: riskData.riskLevel === 'none' ? '' : riskData.notes,
            }
          : row
      )
    );

    setRiskNotesByVisor((prev) => {
      const next = { ...prev };
      if (riskData.riskLevel === 'none') {
        delete next[visorRef];
      } else {
        next[visorRef] = riskData.notes || '';
      }
      return next;
    });

    closeRiskLevelModal();
  };

  const renderSortArrow = key => {
    if (sortColumn !== key) return <FaSort className="reports-font-style-grey" />;
    return sortAsc ? ' ▲' : ' ▼';
  };

  const getCategoryBadgeClass = category => {
    if (!category || category === '-') return 'is-empty';
    if (category.length === 1) return 'is-compact';
    return 'is-filled';
  };

  const getCategorySeverityClass = category => {
    const normalized = (category || '').trim().toUpperCase();
    if (!/^[A-S]$/.test(normalized)) return 'severity-unknown';

    const rank = normalized.charCodeAt(0) - 65;
    if (rank <= 2) return 'severity-critical';
    if (rank <= 5) return 'severity-high';
    if (rank <= 9) return 'severity-elevated';
    if (rank <= 13) return 'severity-guarded';
    return 'severity-low';
  };

  const getCategorySortValue = category => {
    const normalized = (category || '').trim().toUpperCase();
    if (!/^[A-S]$/.test(normalized)) return Number.MAX_SAFE_INTEGER;
    return normalized.charCodeAt(0) - 65;
  };

  const getRiskSortValue = riskLevel => {
    const normalized = (riskLevel || '').toString().trim().toLowerCase();
    if (normalized === 'high') return 0;
    if (normalized === 'medium') return 1;
    if (normalized === 'none') return 2;
    return Number.MAX_SAFE_INTEGER;
  };

  const getFavouriteSortValue = isFavourites => (
    isFavourites ? 0 : 1
  );

  const getFavouriteNotes = (item) => {
    const note = [
      item.favouriteNotes,
      item.favoriteNotes,
      item.favouritesNotes,
      item.favouriteDescription,
      item.favoriteDescription,
      item.favouritesDescription,
      item.favouriteComments,
      item.favoriteComments,
      item.comments,
      item.comment,
      item.isFavourites ? item.notes : '',
    ].find((value) => value !== undefined && value !== null && String(value).trim());

    return note ? String(note).trim() : '';
  };

  const getFavouriteEntry = (item) => ({
    ...item,
    visorRef: item?.visorRef,
    deviceId: item?.visorRef,
    isFavourite: Boolean(item?.isFavourites),
    notes: getFavouriteNotes(item),
  });

  const handleFavouriteSave = async (favouriteData) => {
    const record = favouriteModal.record;
    const deviceId = String(record?.visorRef || '').trim();
    if (!deviceId) return;

    const wasFavourite = Boolean(record?.isFavourites);
    const nextNotes = favouriteData.notes.trim();

    if (favouriteData.isFavourite && wasFavourite) {
      await deviceService.updateFavourites({
        deviceId,
        isFavourite: 1,
        favouriteNotes: nextNotes,
      });
    } else if (favouriteData.isFavourite) {
      await deviceService.setFavourites({
        deviceId,
        isFavourite: 1,
        favouriteNotes: nextNotes,
      });
    } else if (wasFavourite) {
      await deviceService.deleteFavourites(deviceId);
    }

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.visorRef === deviceId
          ? {
              ...row,
              isFavourites: favouriteData.isFavourite,
              favouriteNotes: favouriteData.isFavourite ? nextNotes : '',
            }
          : row
      )
    );

    closeFavouriteModal();
  };

  const getRiskNotes = useCallback((item) => {
    const note = [
      riskNotesByVisor[item.visorRef],
      item.description,
      item.Description,
      item.riskData?.description,
      item.riskData?.Description,
      item.riskFlag?.description,
      item.riskFlag?.Description,
      item.riskDescription,
      item.riskFlagDescription,
      item.riskNotes,
      item.notes,
    ].find((value) => value !== undefined && value !== null && String(value).trim());

    return note ? String(note).trim() : '';
  }, [riskNotesByVisor]);

  useEffect(() => {
    if (!canShowRiskLevel || rows.length === 0) return undefined;

    let isCurrent = true;

    const rowsNeedingNotes = rows.filter((row) => {
      const riskLevel = (row.riskLevelDisplay || '').toLowerCase();
      return (
        row.visorRef &&
        (riskLevel === 'high' || riskLevel === 'medium') &&
        !getRiskNotes(row) &&
        !(row.visorRef in riskNotesByVisor)
      );
    });

    if (rowsNeedingNotes.length === 0) return undefined;

    const fetchRiskNotes = async () => {
      const results = await Promise.allSettled(
        rowsNeedingNotes.map(async (row) => {
          const response = await riskService.getRiskFlags({ deviceId: row.visorRef });
          const riskFlag = Array.isArray(response)
            ? response[0]
            : Array.isArray(response?.data)
              ? response.data[0]
              : response?.data && typeof response.data === 'object'
                ? response.data
                : response;

          return [row.visorRef, riskFlag?.description || riskFlag?.Description || ''];
        })
      );

      if (!isCurrent) return;

      setRiskNotesByVisor((prev) => {
        const next = { ...prev };
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const [visorRef, description] = result.value;
            next[visorRef] = description;
          } else {
            next[rowsNeedingNotes[index].visorRef] = '';
          }
        });
        return next;
      });
    };

    fetchRiskNotes();

    return () => {
      isCurrent = false;
    };
  }, [canShowRiskLevel, rows, riskNotesByVisor, getRiskNotes]);

  const sortedRows = useMemo(() => {
    if (
      sortColumn === 'isFavourites' ||
      (canShowRiskLevel && (sortColumn === 'riskLevel' || sortColumn === 'category'))
    ) {
      const sorted = [...rows].sort((a, b) => {
        let aVal, bVal;
        
        if (sortColumn === 'isFavourites') {
          aVal = getFavouriteSortValue(a.isFavourites);
          bVal = getFavouriteSortValue(b.isFavourites);
        } else if (sortColumn === 'riskLevel') {
          aVal = getRiskSortValue(a.riskLevelDisplay);
          bVal = getRiskSortValue(b.riskLevelDisplay);
        } else {
          aVal = getCategorySortValue(a.category);
          bVal = getCategorySortValue(b.category);
        }
        
        if (aVal < bVal) return sortAsc ? -1 : 1;
        if (aVal > bVal) return sortAsc ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return rows;
  }, [canShowRiskLevel, rows, sortColumn, sortAsc]);

  const derivedRows = sortedRows;
  const derivedTotalPages = totalPages;
  const paginatedRows = derivedRows;
  const derivedGroupCount = groupCount;
  const derivedDeviceCount = deviceCount;

  const startRecord = derivedGroupCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord   = Math.min(currentPage * pageSize, derivedGroupCount);

  return (
    <div>
      <AdvancedSearch />

      <div className="record-custom-container incidents-table-custom-container">
        {/* Pagination Controls */}
        <div className="action-bar">
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(1)} disabled={currentPage <= 1}>
              First
            </button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>
              Previous
            </button>
            <input
              type="number"
              value={currentPage}
              onChange={e => handlePageChange(Number(e.target.value))}
              min={1}
              max={derivedTotalPages}
            />
            <b>/</b> <b>{derivedTotalPages}</b>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= derivedTotalPages}>
              Next
            </button>
            <button onClick={() => handlePageChange(derivedTotalPages)} disabled={currentPage >= derivedTotalPages}>
              Last
            </button>
            <select value={pageSize} onChange={handlePageSizeChange}>
              {[10, 20, 50, 100, 200].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Records Table Section */}
        <div className="record-table-section">
          <div className="record-table-container">
            <div className="table-wrapper">
              {fetchLoading && (
                <div className="loading-spinner-overlay">
                  <div className="loading-spinner" />
                </div>
              )}

              {!fetchLoading && (fetchError || derivedRows.length === 0) ? (
                <div className="table-message-container">
                  <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    size="3x"
                                    className="message-icon"
                                  />
                  <p className="table-message-text">
                    {fetchError || 'There are no images queued for review at this time.'}
                  </p>
                </div>
              ) : (
                <table className="record-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('groupName')}>
                        Group Name {renderSortArrow('groupName')}
                      </th>
                      <th className="incidents-table__visor-header" onClick={() => handleSort('visorRef')}>
                        <span className="incidents-table__visor-header-content">
                          {isPoliceUser && (
                            <button
                              type="button"
                              className="incidents-table__favourite-sort"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSort('isFavourites');
                              }}
                              title="Sort by favourite"
                              aria-label="Sort by favourite"
                            >
                              <FaStar />
                              {sortColumn === 'isFavourites' ? (sortAsc ? ' ▲' : ' ▼') : <FaSort className="reports-font-style-grey" />}
                            </button>
                          )}
                          <span>Visor Reference {renderSortArrow('visorRef')}</span>
                        </span>
                      </th>
                      {canShowRiskLevel && (
                        <>
                          <th onClick={() => handleSort('riskLevel')}>
                            Risk Level {renderSortArrow('riskLevel')}
                          </th>
                          <th onClick={() => handleSort('category')}>
                            Category {renderSortArrow('category')}
                          </th>
                        </>
                      )}
                      <th>Profile</th>
                      <th className="count-clickable-header" onClick={() => handleSort('recordCount')}>
                        Record Count {renderSortArrow('recordCount')}
                        {derivedGroupCount > 0 && (
                          <span
                            className="count-circle-header clickable-count"
                            onClick={e => { e.stopPropagation(); handleTotalClick(); }}
                          >
                            {derivedGroupCount}
                          </span>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((item, idx) => {
                      const riskLevel = item.riskLevelDisplay || 'N/A';
                      const category = item.category || '-';
                      const riskNotes = getRiskNotes(item);
                      const riskTooltipId = `risk-tooltip-${item.visorRef || idx}`;
                      const favouriteNotes = getFavouriteNotes(item);
                      const favouriteTooltipId = `favourite-tooltip-${item.visorRef || idx}`;
                      return (
                      <tr key={idx}>
                        <td>
                          <FaSitemap className="reports-font-style-grey" /> {item.groupName}
                        </td>
                        <td>
                          <div className="users-table__visor-cell">
                            {isPoliceUser && (
                              <button
                                type="button"
                                className={`users-table__favourite-button incidents-table__favourite-button ${
                                  item.isFavourites ? 'is-favourite' : ''
                                }`}
                                onClick={() => openFavouriteModal(item)}
                                aria-describedby={favouriteTooltipId}
                                aria-label={
                                  item.isFavourites
                                    ? `Edit favourite for ${item.visorRef}`
                                    : `Add favourite for ${item.visorRef}`
                                }
                              >
                                {item.isFavourites ? <FaStar /> : <FaRegStar />}
                                <span className="favourite-tooltip" id={favouriteTooltipId} role="tooltip">
                                  <span className="favourite-tooltip-title">Favourite notes</span>
                                  <span className="favourite-tooltip-body">
                                    {favouriteNotes || 'No favourite notes have been recorded for this visor.'}
                                  </span>
                                </span>
                              </button>
                            )}
                            <span className="users-table__visor-text">
                              <FaUser className="reports-font-style-grey" /> {item.visorRef}
                            </span>
                          </div>
                        </td>
                        {canShowRiskLevel && (
                          <>
                            <td className="risk-level-cell">
                              <button
                                type="button"
                                tabIndex={0}
                                className={`risk-level-button-rec ${
                                  riskLevel?.toLowerCase() === 'high' ? 'risk-high' :
                                  riskLevel?.toLowerCase() === 'medium' ? 'risk-medium' :
                                  'risk-none'
                                }`}
                                aria-describedby={riskTooltipId}
                                onClick={() => openRiskLevelModal(item)}
                                title="Edit Risk Level"
                              >
                                {riskLevel}
                                <span className="risk-tooltip" id={riskTooltipId} role="tooltip">
                                  <span className="risk-tooltip-title">Risk notes</span>
                                  <span className="risk-tooltip-body">
                                    {riskNotes || 'No risk notes have been recorded for this flag.'}
                                  </span>
                                </span>
                              </button>
                            </td>
                            <td className="risk-category-cell">
                              <span
                                className={`risk-category-badge ${getCategoryBadgeClass(category)} ${getCategorySeverityClass(category)}`}
                                title={category === '-' ? 'No category assigned' : `Category ${category}`}
                              >
                                {category}
                              </span>
                            </td>
                          </>
                        )}
                        <td>
                          <button
                            className="btn-view-profile"
                            onClick={() => handleViewProfile(item.visorRef)}
                          >
                            View <FaExternalLinkAlt className="records-font-style-grey" />
                          </button>
                        </td>                   
                        <td
                          className="record-count clickable-count"
                          onClick={() => handleRecordCountClick(item.visorRef)}
                        >
                          <button className="btn-view">
                            <span className="count-circle">{item.recordCount}</span> View
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`table-footer ${derivedGroupCount > 0 ? 'has-entries' : 'is-empty'}`}>
          {derivedGroupCount > 0 && (
            <p className="entries-info table-footer-pill">
              {`Showing ${startRecord} to ${endRecord} of ${derivedDeviceCount} devices`}
            </p>
          )}
        </div>
      </div>
      {canShowRiskLevel && riskLevelModal.open && riskLevelModal.nominal && (
        <RiskLevelModal
          isOpen={riskLevelModal.open}
          nominal={riskLevelModal.nominal}
          onClose={closeRiskLevelModal}
          onSave={handleRiskLevelSave}
        />
      )}
      {isPoliceUser && favouriteModal.open && favouriteModal.record && (
        <FavouriteDeviceModal
          isOpen={favouriteModal.open}
          user={{
            ...favouriteModal.record,
            visorId: favouriteModal.record.visorRef,
          }}
          initialValue={getFavouriteEntry(favouriteModal.record)}
          onClose={closeFavouriteModal}
          onSave={async (favouriteData) => {
            try {
              await handleFavouriteSave(favouriteData);
            } catch (error) {
              console.error('Failed to save favourite', error);
              window.alert('Failed to save favourite. Please try again.');
            }
          }}
        />
      )}
    </div>
  );
};

export default RecordsTable;
